import { createContext, useCallback, useMemo, useReducer } from "react";
import { Card, Col, Row, Button } from "react-bootstrap";
import {
  CANCEL,
  COMPLETE_PATIENT_REVIEW,
  PATIENT_REVIEW_CREATE,
  WEEKLY_REVIEW_OFFSET,
} from "../../util/const";
import { Patient } from "../../types/patient";
import { DatabaseManager } from "../../services/DatabaseManager";
import PatientData from "../PatientData";
import { usePatientReview } from "../../services/useData";
import { GetCurrentDate, ProcessReviewData } from "../../util/data-utils";
import ReviewOutcome from "../ReviewOutcome";
import ReviewHistory from "../ReviewHistory";
import LoadingSpinner from "../../Components/LoadingSpinner";


export const ReviewContext = createContext({
  patient: {} as Patient,
  weekEnding: "",
  moreData: (endAtDate: string) => {},
  reload: () => {},
});

export type ReviewProps = {
  toggleDisplay: (didComplete: boolean) => void;
  patients: Patient[];
};

const ReviewPatient = (props: ReviewProps) => {

  const today = GetCurrentDate();

  const reviewState = { reviewingPatients:props.patients, index:0, weekEnding:today }
  const enum REDUCER_ACTION_TYPE {
    PATIENT_CHANGED,
    WEEKEND_CHANGED,
    REVIEW_COMPLETE
  }
  type ReducerAction = {
    type: REDUCER_ACTION_TYPE,
    payload: {
      index?: number,
      endAt?: string
    }
  }
  const reducer = (state: typeof reviewState, action: ReducerAction): typeof 
  reviewState => {
    switch(action.type)
    {
      case REDUCER_ACTION_TYPE.PATIENT_CHANGED: {
        return {...state, reviewingPatients:state.reviewingPatients, index: action.payload.index!, weekEnding: today}
      }        
      case REDUCER_ACTION_TYPE.WEEKEND_CHANGED: {
        return {...state, reviewingPatients:state.reviewingPatients, index: state.index, weekEnding: action.payload.endAt!}
      }
      case REDUCER_ACTION_TYPE.REVIEW_COMPLETE: {
        var index = state.index;
        const resourceId = state.reviewingPatients[index].resourceId;
        var reviewingPatients = state.reviewingPatients.filter((reviewPatient: Patient) => {
          return reviewPatient.resourceId !== resourceId;
        });

        if (index > reviewingPatients.length) index = reviewingPatients.length - 1;
        else if (index === reviewingPatients.length) index--;

        return {...state, reviewingPatients:reviewingPatients, index: index, weekEnding: today}
      }
      default: throw Error();
    }
  }
  const [review, dispatch] = useReducer(reducer, reviewState);

  /// Fetch the data - this will re-run each time a parameter changes
  const fetchedData = usePatientReview({
    id: review.reviewingPatients[review.index] ? review.reviewingPatients[review.index].resourceId : 0,
    endDate: review.weekEnding,
    offset: WEEKLY_REVIEW_OFFSET
  });

  /// Only process the data if it has finished loading
  if(!fetchedData.isLoading && !fetchedData.isValidating && review.reviewingPatients.length > 0)
    ProcessReviewData(fetchedData.review, review.reviewingPatients[review.index]);

  const onNextPage = () => {
    dispatch({type: REDUCER_ACTION_TYPE.PATIENT_CHANGED, payload: {index: review.index + 1}});
  };

  const onPrevPage = () => {
    dispatch({type: REDUCER_ACTION_TYPE.PATIENT_CHANGED, payload: {index: review.index - 1}});
  };

  const moreData = useCallback((endAtDate: string) => {
    dispatch({type: REDUCER_ACTION_TYPE.WEEKEND_CHANGED, payload: {endAt: endAtDate}});
  }, [REDUCER_ACTION_TYPE.WEEKEND_CHANGED]);

  const reload = useCallback(() => {
    fetchedData.mutate();
  }, [fetchedData]);

  const completeReview = () => {
    review.reviewingPatients[review.index].currentReview.reviewDate = new Date().toISOString();
    DatabaseManager.getInstance()
      .post(PATIENT_REVIEW_CREATE, patient)
      .then((response) => {
        // forces data change to display in the history table
        fetchedData.mutate();

        // clear the patient review
        patient.currentReview = {
          reviewDate: "",
          message: "",
          note: "",
          messageRead: false,
          enteredInError: false
        };

        // remove the patient we just saved
        var filtered = review.reviewingPatients.filter((reviewPatient: Patient) => {
          return reviewPatient.resourceId !== patient.resourceId;
        });

        if(filtered.length === 0)
          props.toggleDisplay(true);
        else
          dispatch({type: REDUCER_ACTION_TYPE.REVIEW_COMPLETE, payload: {}});
      })
      .catch(() => {
        console.log("completeReview save error");
      });
  };

  const patient = review.reviewingPatients[review.index];
  const weekEnding = review.weekEnding;
  const reviewContextValue = useMemo(
    () => ({ patient, weekEnding, moreData, reload }),
    [patient, weekEnding, moreData, reload]
  );

  if(fetchedData.isLoading)
  {
    return (<LoadingSpinner />);
  }

  return (
    <Card className="mt-3 mb-3" border="secondary" text="white">
      <Card.Header className="fs-5 app-colour">
        <Row>
          <Col>
            Reviewing: {review.index + 1} of {review.reviewingPatients.length}
          </Col>
          <Col></Col>
          <Col className="right">
            <Button
              type="button"
              className="fs-6 btn-outline-white"
              onClick={onPrevPage}
              disabled={review.index === 0}>
              Prev Patient
            </Button>
          </Col>
          <Col className="text-center">
            {review.reviewingPatients[review.index]?.familyName!.toUpperCase()}, {review.reviewingPatients[review.index]?.givenNames}
            <div className="mt-2"></div>
          </Col>
          <Col>
            <Button
              type="button"
              className="fs-6 btn-outline-white"
              onClick={onNextPage}
              disabled={review.index === review.reviewingPatients.length - 1}>
              Next Patient
            </Button>
          </Col>
          <Col></Col>
          <Col className="right">
            <Button
              type="button"
              onClick={(e) => {
                props.toggleDisplay(false);
              }}
              className="fs-6 btn-outline-white me-2">
              {CANCEL}
            </Button>
            <Button
              type="button"
              onClick={() => {
                completeReview();
              }}
              className="fs-6 btn-outline-white">
              {COMPLETE_PATIENT_REVIEW}
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <ReviewContext.Provider value={reviewContextValue}>
          <PatientData />
          <ReviewOutcome />
          <ReviewHistory />
        </ReviewContext.Provider>
      </Card.Body>
    </Card>
  );
};

export default ReviewPatient;